import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { numberToMoney } from "@/util/moneyConversion";
import {
  type OrderOutput,
  OrderPaymentStatus,
  OrderStatus,
  orderStatusTranslation,
} from "@/interfaces";
import {
  TrendingUp,
  ShoppingBag,
  CreditCard,
  AlertCircle,
  PieChart,
} from "lucide-react";

interface OrderTotalsProps {
  orders?: OrderOutput[];
}

export function OrderTotals({ orders }: Readonly<OrderTotalsProps>) {
  const totalOrders = orders?.length ?? 0;
  const totalValue =
    orders?.reduce((sum, order) => sum + (order.totalAmount || 0), 0) ?? 0;
  const averageOrderValue = totalOrders > 0 ? totalValue / totalOrders : 0;
  const pendingOrders =
    orders?.filter((order) =>
      [OrderPaymentStatus.PENDING, OrderPaymentStatus.PARTIALLY_PAID].includes(
        order.paymentStatus
      )
    ).length ?? 0;

  // Calculate order status counts in a safer way
  const statusCounts: Record<string, number> = {};

  // Initialize all possible statuses with zero count
  Object.values(OrderStatus).forEach((status) => {
    statusCounts[status] = 0;
  });

  // Count occurrences of each status
  orders?.forEach((order) => {
    if (order.status) {
      statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
    }
  });

  // Create an array of status percentages for visualization
  const statusPercentages = Object.entries(statusCounts)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, count]) => count > 0) // Only include statuses that have orders
    .map(([status, count]) => ({
      status: status as OrderStatus,
      label: orderStatusTranslation[status as OrderStatus] || status,
      count,
      percentage: totalOrders > 0 ? (count / totalOrders) * 100 : 0,
    }));

  // Sort by count (highest first)
  statusPercentages.sort((a, b) => b.count - a.count);

  // Status colors mapping
  const statusColors: Record<string, string> = {
    [OrderStatus.PENDING]: "bg-yellow-500",
    [OrderStatus.SCHEDULED]: "bg-blue-500",
    [OrderStatus.IN_PREPARATION]: "bg-orange-500",
    [OrderStatus.PREPARED]: "bg-teal-500",
    [OrderStatus.DONE]: "bg-green-500",
    [OrderStatus.CANCELED]: "bg-red-500",
  };

  return (
    <div className="grid gap-4 mb-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm bg-white dark:bg-gray-950">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Total de Pedidos
            </CardTitle>
            <div className="rounded-full p-2 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
              <ShoppingBag className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOrders}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Pedidos registrados
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-gray-950">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Valor Total
            </CardTitle>
            <div className="rounded-full p-2 bg-green-50 dark:bg-green-950/50 text-green-600 dark:text-green-400">
              <CreditCard className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{numberToMoney(totalValue)}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Valor total dos pedidos
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-gray-950">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Valor Médio
            </CardTitle>
            <div className="rounded-full p-2 bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400">
              <TrendingUp className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {numberToMoney(averageOrderValue)}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Valor médio por pedido
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-gray-950">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Pagamentos Pendentes
            </CardTitle>
            <div className="rounded-full p-2 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingOrders}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Pedidos aguardando pagamento
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status Percentages Card */}
      {totalOrders > 0 && (
        <Card className="shadow-sm bg-white dark:bg-gray-950">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Distribuição de Status dos Pedidos
            </CardTitle>
            <div className="rounded-full p-2 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
              <PieChart className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress bar showing status distribution */}
            <div className="h-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden flex">
              {statusPercentages.map((item) => (
                <div
                  key={item.status}
                  className={`h-full ${
                    statusColors[item.status] || "bg-gray-500"
                  }`}
                  style={{ width: `${item.percentage}%` }}
                />
              ))}
            </div>

            {/* Status legend with counts and percentages */}
            <div className="grid gap-3">
              {statusPercentages.map((item) => (
                <div
                  key={item.status}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        statusColors[item.status] || "bg-gray-500"
                      }`}
                    />
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {item.count} pedidos
                    </span>
                    <div className="w-16 text-right">
                      <span className="text-sm font-semibold">
                        {item.percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
